import React, { useCallback } from 'react';
import * as Styled from './ActiveChat.styled';
import { useSelector, useDispatch } from 'react-redux';
import {
  ChatStates,
  chatErrorSelector,
  completingSessionStatusSelector,
  isIteratingBranchingNodesSelector,
  resendDiagnosisFormDataSelector,
  resendFormDataSelector,
  resendGreetingSelector,
  selectChatState,
  selectLatestBotTextChat,
  selectLatestTextChat,
  selectLatestUserTextChat,
  sendChat,
  sendingChatTextSelector,
  setScoreOverviewVisible,
  virtualHumanTalkingSelector,
  waitingOnUserContinueSelector,
} from '../../../slices/chat/ChatSlice';
import ChatLoadingIndicator from './ChatLoadingIndicator/ChatLoadingIndicator';
import ChatItem from './ChatItem/ChatItem';
import { Button, Icon } from '@virtidev/toolbox';
import {
  checkDiagnosis,
  diagnosisPromptSelector,
} from '../../../slices/DiagnosisSlice';
import ChatRequestError from '../../ChatRequestError/ChatRequestError';
import {
  virtualHumanDataSelector,
  virtualHumanTypeSelector,
} from '../../../slices/VirtualHumanSlice';
import { useVHTypeConfig } from '../../vh-types/vhTypeHooks';
import { GetFormattedTime } from '@base/utility/TimeFormatting';
import { GetShouldGenerateAudio } from '@vh/utility/isVHAudioStreamService';
import { useTranslation } from 'react-i18next';

const ActiveChat = () => {
  const dispatch = useDispatch();
  const isSendingChat = useSelector((state) => state.chat.status === 'pending');

  const handleViewSummary = useCallback(() => {
    dispatch(setScoreOverviewVisible(true));
  }, [dispatch]);

  const latestUserTextChat = useSelector(selectLatestUserTextChat);
  const lastTextChat = useSelector(selectLatestTextChat);

  const isIteratingBranchingNodes = useSelector(
    isIteratingBranchingNodesSelector
  );
  const latestBotChat = useSelector(selectLatestBotTextChat);
  const chatState = useSelector(selectChatState);
  const vhTalking = useSelector(virtualHumanTalkingSelector);
  const sessionComplete = ChatStates.COMPLETE === chatState && !vhTalking;
  const finalScores = useSelector((state) => state.chat.finalScores);
  const diagnosisPrompt = useSelector(diagnosisPromptSelector);
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const completingSessionStatus = useSelector(completingSessionStatusSelector);
  const chatError = useSelector(chatErrorSelector);
  const resendGreeting = useSelector(resendGreetingSelector);
  const { getGreetingThunk } = useVHTypeConfig();
  const resendFormData = useSelector(resendFormDataSelector);
  const resendDiagnosisFormData = useSelector(resendDiagnosisFormDataSelector);
  const vh = useSelector(virtualHumanDataSelector);
  const waitingOnUserContinue = useSelector(waitingOnUserContinueSelector);
  const sendingChatText = useSelector(sendingChatTextSelector);
  const vhType = useSelector(virtualHumanTypeSelector);

  const currentlyInForcedUXIterationWait =
    isIteratingBranchingNodes && lastTextChat?.chatType === 'user';

  const { t } = useTranslation();

  const generateAudio = GetShouldGenerateAudio(
    vh?.VHVoice?.Service,
    vh?.PlayerType
  );
  const resend = useCallback(() => {
    if (chatState === ChatStates.TALK) {
      if (resendGreeting) {
        dispatch(
          getGreetingThunk()({
            vhId: vh.ExternalID,
            generateAudio,
          })
        );
      } else {
        dispatch(sendChat(resendFormData));
      }
    } else if (chatState === ChatStates.DIAGNOSIS && resendDiagnosisFormData) {
      dispatch(checkDiagnosis(resendDiagnosisFormData));
    }
  }, [
    chatState,
    resendDiagnosisFormData,
    resendGreeting,
    dispatch,
    getGreetingThunk,
    vh.ExternalID,
    generateAudio,
    resendFormData,
  ]);

  const chatItemToShow = waitingOnUserContinue
    ? latestUserTextChat
    : latestBotChat;
  let feedbackText = undefined;
  if (!isSendingChat && !isIteratingBranchingNodes && !sessionComplete) {
    if (vhType === 'branching') {
      feedbackText = lastTextChat?.feedbackText ?? undefined;
    } else {
      feedbackText = latestBotChat?.feedbackText ?? undefined;
    }
  }

  return (
    <Styled.ChatLog>
      {!sessionComplete &&
        latestBotChat?.text &&
        // !awaitingUnityAudioPlay &&
        !isSendingChat &&
        !currentlyInForcedUXIterationWait &&
        chatState !== ChatStates.DIAGNOSIS && (
          <ChatItem chatLog={chatItemToShow} />
        )}
      {!sessionComplete && feedbackText && (
        <Styled.FeedbackTextWrapper>
          <Styled.FeedbackText>
            <Icon icon="clipboard" type="outline" size={'1.4rem'} />
            <div>{feedbackText}</div>
          </Styled.FeedbackText>
        </Styled.FeedbackTextWrapper>
      )}
      {(isSendingChat || currentlyInForcedUXIterationWait) && (
        // || awaitingUnityAudioPlay
        <ChatLoadingIndicator
          sendingText={sendingChatText ?? latestUserTextChat?.text}
        />
      )}
      {chatState === ChatStates.DIAGNOSIS && (
        <ChatItem>{diagnosisPrompt}</ChatItem>
      )}
      {
        // !awaitingUnityAudioPlay &&
        !isSendingChat && sessionComplete && (
          <ChatItem>
            <Styled.SessionCompleteItemContent>
              {t('completedIn', {
                time: GetFormattedTime(finalScores?.timeSpent),
              })}
              <Button color="turquoise" onClick={handleViewSummary}>
                {t('viewSummary')}
              </Button>
            </Styled.SessionCompleteItemContent>
          </ChatItem>
        )
      }
      {chatError &&
        vhType !== 'branching' &&
        chatLoadingStatus === 'idle' &&
        completingSessionStatus === 'idle' && (
          <ChatRequestError onResend={resend} error={chatError} />
        )}
    </Styled.ChatLog>
  );
};
export default ActiveChat;
